import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`
  },
  professionalSelect: {
    marginBottom: theme.spacing(2),
    maxWidth: 320
  },
  dayRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  dayLabel: {
    width: 100,
    fontWeight: 600
  }
}));

const weekdays = [
  { value: 0, label: "Domingo" },
  { value: 1, label: "Segunda" },
  { value: 2, label: "Terça" },
  { value: 3, label: "Quarta" },
  { value: 4, label: "Quinta" },
  { value: 5, label: "Sexta" },
  { value: 6, label: "Sábado" }
];

const emptyDayState = weekdays.reduce((acc, day) => {
  acc[day.value] = {
    id: null,
    active: false,
    startTime: "09:00",
    endTime: "18:00",
    breakStart: "",
    breakEnd: ""
  };
  return acc;
}, {});

const AvailabilityManager = () => {
  const classes = useStyles();
  const [professionals, setProfessionals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [professionalLocations, setProfessionalLocations] = useState([]);
  const [selectedProfessional, setSelectedProfessional] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [days, setDays] = useState(emptyDayState);

  useEffect(() => {
    async function loadSetupData() {
      try {
        const [
          professionalsResponse,
          locationsResponse,
          linksResponse
        ] = await Promise.all([
          api.get("/users/list"),
          api.get("/agenda-locations"),
          api.get("/professional-locations")
        ]);

        setProfessionals(
          professionalsResponse.data || []
        );

        setLocations(
          locationsResponse.data || []
        );

        setProfessionalLocations(
          linksResponse.data || []
        );
      } catch (err) {
        toastError(err);
      }
    }

    loadSetupData();
  }, []);

  const fetchAvailability = useCallback(async (
    professionalId,
    locationId
  ) => {
    if (!professionalId) return;

    try {
      const params = {
        professionalId
      };

      if (locationId) {
        params.locationId = locationId;
      }

      const { data } = await api.get(
        "/professional-availability",
        {
          params
        }
      );

      const newDays = { ...emptyDayState };
      Object.keys(newDays).forEach(key => {
        newDays[key] = { ...emptyDayState[key] };
      });

      data.forEach(item => {
        newDays[item.weekday] = {
          id: item.id,
          active: item.active,
          startTime: item.startTime?.slice(0, 5) || "09:00",
          endTime: item.endTime?.slice(0, 5) || "18:00",
          breakStart: item.breakStart ? item.breakStart.slice(0, 5) : "",
          breakEnd: item.breakEnd ? item.breakEnd.slice(0, 5) : ""
        };
      });

      setDays(newDays);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    const resetDays = () => {
      const cleanDays = {
        ...emptyDayState
      };

      Object.keys(cleanDays).forEach(key => {
        cleanDays[key] = {
          ...emptyDayState[key]
        };
      });

      setDays(cleanDays);
    };

    if (!selectedProfessional) {
      setSelectedLocation("");
      resetDays();
      return;
    }

    const activeLocations =
      locations.filter(
        location =>
          location.active !== false
      );

    /*
     * Compatibilidade com empresas antigas:
     * se ainda não existem unidades,
     * continua usando a disponibilidade legada.
     */
    if (!activeLocations.length) {
      setSelectedLocation("");

      fetchAvailability(
        selectedProfessional
      );

      return;
    }

    const linkedLocationIds =
      professionalLocations
        .filter(
          link =>
            Number(link.professionalId) ===
            Number(selectedProfessional)
        )
        .map(link =>
          Number(link.locationId)
        );

    const eligibleLocations =
      activeLocations.filter(location =>
        linkedLocationIds.includes(
          Number(location.id)
        )
      );

    /*
     * Uma única unidade:
     * seleção automática.
     */
    if (eligibleLocations.length === 1) {
      const locationId =
        Number(
          eligibleLocations[0].id
        );

      setSelectedLocation(
        locationId
      );

      fetchAvailability(
        selectedProfessional,
        locationId
      );

      return;
    }

    /*
     * Mais de uma unidade:
     * aguarda a escolha na interface.
     */
    if (
      selectedLocation &&
      eligibleLocations.some(
        location =>
          Number(location.id) ===
          Number(selectedLocation)
      )
    ) {
      fetchAvailability(
        selectedProfessional,
        selectedLocation
      );

      return;
    }

    setSelectedLocation("");
    resetDays();
  }, [
    selectedProfessional,
    selectedLocation,
    locations,
    professionalLocations,
    fetchAvailability
  ]);

  const hasActiveLocations =
    locations.some(
      location =>
        location.active !== false
    );

  const linkedLocationIds =
    professionalLocations
      .filter(
        link =>
          Number(link.professionalId) ===
          Number(selectedProfessional)
      )
      .map(link =>
        Number(link.locationId)
      );

  const eligibleLocations =
    locations.filter(
      location =>
        location.active !== false &&
        linkedLocationIds.includes(
          Number(location.id)
        )
    );


  const handleDayChange = (weekday, field, value) => {
    setDays(prev => ({
      ...prev,
      [weekday]: { ...prev[weekday], [field]: value }
    }));
  };

  const handleToggleActive = async weekday => {
    const day = days[weekday];
    const newActive = !day.active;

    if (!selectedProfessional) {
      return;
    }

    if (
      hasActiveLocations &&
      !selectedLocation
    ) {
      toast.error(
        "Selecione uma unidade"
      );
      return;
    }

    handleDayChange(
      weekday,
      "active",
      newActive
    );

    try {
      if (day.id) {
        await api.put(`/professional-availability/${day.id}`, {
          active: newActive,
          locationId:
            selectedLocation || null
        });
      } else if (newActive) {
        const { data } = await api.post("/professional-availability", {
          professionalId: selectedProfessional,
          locationId:
            selectedLocation || null,
          weekday,
          startTime: `${day.startTime}:00`,
          endTime: `${day.endTime}:00`,
          breakStart: day.breakStart ? `${day.breakStart}:00` : null,
          breakEnd: day.breakEnd ? `${day.breakEnd}:00` : null,
          active: true
        });
        handleDayChange(weekday, "id", data.id);
      }
      toast.success("Disponibilidade atualizada");
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveDay = async weekday => {
    const day = days[weekday];

    if (!selectedProfessional) {
      toast.error("Selecione um profissional");
      return;
    }

    if (
      hasActiveLocations &&
      !selectedLocation
    ) {
      toast.error(
        "Selecione uma unidade"
      );
      return;
    }

    const payload = {
      professionalId: selectedProfessional,
      locationId:
        selectedLocation || null,
      weekday,
      startTime: `${day.startTime}:00`,
      endTime: `${day.endTime}:00`,
      breakStart: day.breakStart ? `${day.breakStart}:00` : null,
      breakEnd: day.breakEnd ? `${day.breakEnd}:00` : null,
      active: true
    };

    try {
      if (day.id) {
        await api.put(`/professional-availability/${day.id}`, payload);
      } else {
        const { data } = await api.post("/professional-availability", payload);
        handleDayChange(weekday, "id", data.id);
      }
      handleDayChange(weekday, "active", true);
      toast.success("Disponibilidade salva");
    } catch (err) {
      toastError(err);
    }
  };

  const handleRemoveDay = async weekday => {
    const day = days[weekday];
    if (!day.id) return;

    try {
      await api.delete(`/professional-availability/${day.id}`);
      setDays(prev => ({ ...prev, [weekday]: { ...emptyDayState[weekday] } }));
      toast.success("Disponibilidade removida");
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Paper className={classes.paper} variant="outlined">
      <Grid
        container
        spacing={2}
        style={{
          marginBottom: 8
        }}
      >
        <Grid item xs={12} sm={6}>
          <FormControl
            variant="outlined"
            fullWidth
          >
            <InputLabel>
              Profissional
            </InputLabel>

            <Select
              value={
                selectedProfessional
              }
              label="Profissional"
              onChange={e => {
                setSelectedProfessional(
                  e.target.value
                );

                setSelectedLocation("");
              }}
            >
              {professionals.map(p => (
                <MenuItem
                  key={p.id}
                  value={p.id}
                >
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {hasActiveLocations && (
          <Grid item xs={12} sm={6}>
            <FormControl
              variant="outlined"
              fullWidth
              disabled={
                !selectedProfessional ||
                !eligibleLocations.length
              }
            >
              <InputLabel>
                Unidade
              </InputLabel>

              <Select
                value={
                  selectedLocation
                }
                label="Unidade"
                onChange={e =>
                  setSelectedLocation(
                    e.target.value
                  )
                }
              >
                {eligibleLocations.map(
                  location => (
                    <MenuItem
                      key={location.id}
                      value={location.id}
                    >
                      {location.name}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      {!selectedProfessional && (
        <Typography variant="body2" color="textSecondary">
          Selecione um profissional para configurar a disponibilidade.
        </Typography>
      )}

      {selectedProfessional &&
        hasActiveLocations &&
        !eligibleLocations.length && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{
              marginBottom: 16
            }}
          >
            Este profissional ainda não está vinculado a nenhuma unidade.
          </Typography>
        )}

      {selectedProfessional &&
        hasActiveLocations &&
        eligibleLocations.length > 1 &&
        !selectedLocation && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{
              marginBottom: 16
            }}
          >
            Selecione a unidade para configurar os horários.
          </Typography>
        )}

      {selectedProfessional &&
        (
          !hasActiveLocations ||
          Boolean(selectedLocation)
        ) &&
        weekdays.map(day => {
          const dayState = days[day.value];
          return (
            <div key={day.value} className={classes.dayRow}>
              <Typography className={classes.dayLabel}>{day.label}</Typography>
              <Switch
                checked={dayState.active}
                onChange={() => handleToggleActive(day.value)}
                color="primary"
              />
              <Grid container spacing={1} alignItems="center" style={{ flex: 1 }}>
                <Grid item xs={2}>
                  <TextField
                    label="Início"
                    type="time"
                    value={dayState.startTime}
                    onChange={e => handleDayChange(day.value, "startTime", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="Fim"
                    type="time"
                    value={dayState.endTime}
                    onChange={e => handleDayChange(day.value, "endTime", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="Pausa início"
                    type="time"
                    value={dayState.breakStart}
                    onChange={e => handleDayChange(day.value, "breakStart", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    label="Pausa fim"
                    type="time"
                    value={dayState.breakEnd}
                    onChange={e => handleDayChange(day.value, "breakEnd", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={2}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => handleSaveDay(day.value)}
                  >
                    Salvar
                  </Button>
                </Grid>
                <Grid item xs={2}>
                  {dayState.id && (
                    <IconButton size="small" onClick={() => handleRemoveDay(day.value)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </div>
          );
        })}
    </Paper>
  );
};

export default AvailabilityManager;
